import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router";
import styles from "./ui.module.css";

type Variant = "primary" | "ai" | "approve" | "field" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

function buttonClass(variant: Variant, size: Size, extra?: string): string {
  return [
    styles.button,
    // "ai" is what the model does for you; it wears the shared gradient from tokens.css.
    variant === "ai" ? "zh-ai-btn" : styles[variant],
    size !== "md" ? styles[size] : "",
    "zh-press",
    extra ?? "",
  ]
    .filter(Boolean)
    .join(" ");
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  busy?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = "field",
  size = "md",
  busy,
  icon,
  children,
  className,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      className={buttonClass(variant, size, className)}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <span className={styles.spinner} aria-hidden="true" /> : icon}
      {children}
    </button>
  );
}

export function ButtonLink({
  to,
  variant = "field",
  size = "md",
  icon,
  children,
  className,
}: {
  to: string;
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link to={to} className={buttonClass(variant, size, className)}>
      {icon}
      {children}
    </Link>
  );
}

export type Tone = "neutral" | "card" | "ok" | "hum" | "acc" | "crit" | "solid-hum";

export function Pill({
  tone = "neutral",
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={[styles.pill, styles[`tone-${tone}`], className ?? ""].join(" ")}>
      {children}
    </span>
  );
}

export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div role="group" aria-label={label} className={styles.segmented}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={styles.switch}
      onClick={() => onChange(!checked)}
    >
      <span className={styles.knob} />
    </button>
  );
}

export function Skeleton({
  height = 120,
  width = "100%",
}: {
  height?: number;
  width?: number | string;
}) {
  return <div className={styles.skeleton} style={{ height, width }} aria-hidden="true" />;
}

export function LoadingState({ label = "Loading" }: { label?: string }) {
  return (
    <div className={styles.state} role="status" aria-live="polite">
      <span className={styles.spinner} aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

/** What went wrong, in words a person can act on, from an API problem or a failed fetch. */
function explain(error: unknown): { title: string; text: string; detail: string | null } {
  const status =
    typeof error === "object" && error !== null && "status" in error ? Number(error.status) : null;
  const message = error instanceof Error ? error.message : "";
  if (status === null || Number.isNaN(status)) {
    return {
      title: "ZeaOps can't reach its server",
      text: "Check your connection, then try again.",
      detail: message || null,
    };
  }
  const detail = `${String(status)} ${message}`.trim();
  if (status === 403) {
    return {
      title: "Your role can't see this",
      text: "Ask an admin for a role that can.",
      detail,
    };
  }
  if (status === 404) {
    return {
      title: "This isn't here any more",
      text: "It may have been removed. Go back and pick it again.",
      detail,
    };
  }
  if (status >= 500) {
    return {
      title: "The ZeaOps server didn't answer",
      text: "It may be restarting. Try again in a moment; if it keeps happening, tell whoever runs ZeaOps for your team.",
      detail,
    };
  }
  return {
    title: "This didn't load",
    text: message || "The request didn't complete.",
    detail: null,
  };
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { title, text, detail } = explain(error);
  return (
    <div className={`${styles.state} ${styles.stateError}`} role="alert">
      <span className={styles.stateMark} aria-hidden="true">
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M12 7v6M12 17h.01" strokeWidth={2.4} strokeLinecap="round" />
        </svg>
      </span>
      <span className={styles.stateTitle}>{title}</span>
      <span className={styles.stateText}>{text}</span>
      {detail ? <span className={styles.stateDetail}>{detail}</span> : null}
      {onRetry ? (
        <Button variant="field" size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className={styles.state}>
      <span className={styles.stateTitle}>{title}</span>
      {children}
    </div>
  );
}
